import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'

import { styled } from '~/styled'
import { useCallbackRefState } from '~/utilities/hooks/useCallbackRef'

import { Text } from '../text/Text'

type KeyboardEventWindowOrElement =
  | KeyboardEvent
  | React.KeyboardEvent<HTMLDivElement>
type ShortcutConfig = Partial<KeyboardEvent>

type KeyboardShortcutProps = React.HTMLAttributes<HTMLDivElement> & {
  asChild?: boolean
  config: {
    shortcut: ShortcutConfig
    action: ({
      event,
      shortcut
    }: {
      event: KeyboardEvent | React.KeyboardEvent<HTMLDivElement>
      shortcut: ShortcutConfig
    }) => void
  }[]
  targetWindow?: boolean
  onKeyDown?: (e: KeyboardEventWindowOrElement) => void
}

export const KeyboardShortcut = React.forwardRef<
  HTMLDivElement,
  KeyboardShortcutProps
>(({ asChild, config, targetWindow = false, onKeyDown, ...rest }, ref) => {
  const [targetElRef, setTargetElRef] = useCallbackRefState()
  React.useImperativeHandle(ref, () => targetElRef as HTMLDivElement)

  const handleOnKeydown = React.useCallback(
    (e: KeyboardEventWindowOrElement) => {
      config.forEach(({ shortcut, action }) => {
        if (Object.entries(shortcut).every(([key, value]) => e[key] === value))
          action({ event: e, shortcut })
      })
      onKeyDown?.(e)
    },
    [config, onKeyDown]
  )

  React.useEffect(() => {
    if (targetWindow) window.addEventListener('keydown', handleOnKeydown)

    return () => {
      window.removeEventListener('keydown', handleOnKeydown)
    }
  }, [targetWindow, handleOnKeydown])

  const Component = asChild ? Slot : 'div'

  return (
    <Component
      onKeyDown={targetWindow ? undefined : (handleOnKeydown as any)}
      ref={setTargetElRef}
      {...rest}
    />
  )
})

KeyboardShortcut.displayName = 'KeyboardShortcut'

const StyledKeyboardShortcutIndicator = styled(Text, {
  base: [
    'bg-grey-100',
    'text-text-subtle',
    'px-2',
    'py-0.5',
    'min-w-6',
    'min-h-6',
    'font-normal',
    'inline-flex',
    'justify-center',
    'items-center',
    'rounded-md',
    'shrink-0',
    'capsize-none'
  ]
})

type KeyboardShortcutIndicatorProps = React.ComponentProps<
  typeof StyledKeyboardShortcutIndicator
>

export const KeyboardShortcutIndicator = (
  props: KeyboardShortcutIndicatorProps
) => {
  return <StyledKeyboardShortcutIndicator size="sm" as="kbd" {...props} />
}

KeyboardShortcutIndicator.displayName = 'KeyboardShortcut'
